import { useNavigate } from "react-router-dom";

import {
  Settings,
  BarChart2,
  CheckCircle,
  AlertTriangle,
  Info,
  FileText,
  Pencil,
  PlusCircle,
  Download,
  ClipboardList,
  LoaderPinwheel,
  UtilityPole,
  Flame,
  CircleDashed,
} from "lucide-react";

import { MeterSearch } from "./MeterSearch";
import { useMxDashboard } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";
import { QuickActions, QuickActionItem } from "@shared/components/QuickActions";

const serviceActions = [
  {
    title: "Add Single Meter",
    description: "Register a new meter device in the system",
    route: "/mx/device-management/meter/new",
    icon: PlusCircle,
    color: "bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-200",
  },
  {
    title: "Add Bulk Meters",
    description: "Import multiple meters via spreadsheet",
    route: "/onboarding/data-migration/data-cards",
    icon: Download,
    color: "bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200",
  },
  {
    title: "View All Meters",
    description: "Browse and search the complete meter inventory",
    route: "/mx/device-management/meter",
    icon: ClipboardList,
    color: "bg-green-50 hover:bg-green-100 text-green-800 border border-green-200",
  },
  {
    title: "Raise Service Orders",
    description: "Create maintenance or inspection requests",
    route: "/mx/device-management/service-order",
    icon: ClipboardList,
    color: "bg-purple-50 hover:bg-purple-100 text-purple-800 border border-purple-200",
  },
];

const SummaryCard = ({ title, value, description, icon }) => (
  <div className="bg-white rounded-2xl border p-4 w-full">
    <div className="flex justify-between">
      <div>
        <div className="text-lg font-medium text-muted-foreground">{title}</div>
        <div className="text-2xl font-bold mt-2">{value}</div>
        <div className="text-sm text-gray-500">{description}</div>
      </div>
      <div className="text-2xl">{icon}</div>
    </div>
  </div>
);

export const MxDashboard: React.FC = () => {
  usePageViewTimer({ pageName: "MxDashboard" });
  const navigate = useNavigate();
  const remote_utility_id = useRemoteUtilityId();

  //hook for summary cards
  const { data } = useMxDashboard(remote_utility_id);
  const meterConditions = data?.meterConditions;
  const staticCards = [
    {
      title: "Total Meters",
      value: data?.totalMeters,
      description: "All registered meters",
      icon: <Settings className="w-5 h-5 text-utility-blue" />,
    },
    {
      title: "Total Active Meters",
      value: data?.totalActiveMeters?.count,
      description: `${data?.totalActiveMeters?.newThisMonth ?? ""} this month`,
      icon: <CheckCircle className="w-5 h-5 text-utility-green" />,
    },
  ];

  const getIcon = (type: string) => {
    switch (type) {
      case "water":
        return <LoaderPinwheel className="w-5 h-5 text-utility-blue" />;
      case "electricity":
        return <UtilityPole className="w-5 h-5 text-gray-500" />;
      case "gas":
        return <Flame className="w-5 h-5 text-green-500" />;
      default:
        return <CircleDashed className="w-5 h-5 text-gray-500" />;
    }
  };

  // looping over data to create card according to the meter types present in response.
  const dynamicMeterTypeCards = Object.entries(data?.meterTypes || {}).map(
    ([type, data]) => {
      const meter = data as { count: number; percentage: number };
      return {
        title: `${type.charAt(0).toUpperCase() + type.slice(1)} Meter`,
        value: meter.count,
        description: `${meter.percentage}% of total`,
        icon: getIcon(type),
      };
    }
  );

  const conditionCards = [
    {
      title: "Normal Meters",
      value: meterConditions?.normal.count,
      description: `${meterConditions?.normal.percentage}% of total`,
      icon: <CheckCircle className="w-5 h-5 text-utility-green" />,
    },
    {
      title: "Faulty Meters",
      value: meterConditions?.faulty.count,
      description: `${meterConditions?.faulty.percentage}% of total`,
      icon: <AlertTriangle className="w-5 h-5 text-destructive" />,
    },
    {
      title: "RCNT Meters",
      value: meterConditions?.rcnt.count,
      description: `${meterConditions?.rcnt.percentage}% of total`,
      icon: <Info className="w-5 h-5 text-utility-purple" />,
    },
  ];

  const serviceCards = [
    {
      title: "Pending Service Orders",
      value: data?.pendingServiceOrders,
      description: "Pending Service Order",
      icon: <FileText className="w-5 h-5 text-muted-foreground" />,
    },
    {
      title: "Pending Installations",
      value: data?.pendingInstallations?.result?.total,
      description: "NA",
      icon: <Pencil className="w-5 h-5 text-muted-foreground" />,
    },
  ];

  const cardData = [
    ...staticCards,
    ...dynamicMeterTypeCards,
    ...conditionCards,
    ...serviceCards,
  ];

  // transform serviceActions into QuickActionItem[]
  const quickActions: QuickActionItem[] = serviceActions.map((action) => ({
    title: action.title,
    description: action.description,
    icon: action.icon,
    color: action.color,
    onClick: () => navigate(action.route),
  }));

  // current date and time formatting
  const currentDateTime = new Date()
    .toLocaleString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace("at", " |");

  return (
    <div>
      <div className="">
        <div>
          <h2 className="page-title">Meter Device Summary</h2>
          <p className="page-subtitle">{currentDateTime}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 w-full gap-4 my-4">
          {cardData.map((card, idx) => (
            <SummaryCard key={idx} {...card} />
          ))}
        </div>

        <MeterSearch />

        <div>
          <QuickActions
            actions={quickActions}
            heading={`Meter Services`}
            subheading={`Access meter service modules based on your role and permissions`}
          />
        </div>
      </div>
    </div>
  );
};

export default MxDashboard;
